/**
 * Single/Dual/Trio Bundle Selector - REPLO VERSION
 * Handles Single vs Duo vs Trio option selection, variant radio buttons, and cart integration.
 * This version integrates with Replo's variant selector and product form (form input[name="id"] as source of truth).
 */

class SingleDualBundleSelector {
  constructor(container) {
    this.container = container;
    this.config = this.#loadConfig();
    this.isAddingToCart = false;
    this.isUpdating = false;
    this.intelligemsRefreshTimer = null;
    this.intelligemsRefreshAttempts = 0;
    this.intelligemsRefreshVariantId = '';
    this.isIntelligemsRefreshTicking = false;

    this.optionRadios = this.container.querySelectorAll('[data-option-radio]');
    this.optionCards = this.container.querySelectorAll('[data-option-card]');
    this.optionItemDual = this.container.querySelector('[data-option-item="dual"]');
    this.optionItemTrio = this.container.querySelector('[data-option-item="trio"]');
    this.expandableSlotDual = this.container.querySelector('[data-bundle-expandable-slot="dual"]');
    this.expandableSlotTrio = this.container.querySelector('[data-bundle-expandable-slot="trio"]');
    this.expandableStorage = this.container.querySelector('[data-bundle-expandable-storage]');
    this.bundleExpandable = this.container.querySelector('[data-bundle-expandable]');
    this.bundleInfoText = this.container.querySelector('[data-bundle-info-text]');
    this.bundleGiftFooter = this.container.querySelector('[data-bundle-gift-footer]');
    this.bundleGiftFooterText = this.container.querySelector('[data-bundle-gift-footer-text]');
    this.variantGroup3 = this.container.querySelector('[data-variant-group="3"]');
    this.summaryText = this.container.querySelector('[data-selection-summary]');
    this.optionInput = this.container.querySelector('[data-single-dual-option]');
    this.bundleVariant1Input = this.container.querySelector('[data-bundle-variant="1"]');
    this.bundleVariant2Input = this.container.querySelector('[data-bundle-variant="2"]');
    this.bundleVariant3Input = this.container.querySelector('[data-bundle-variant="3"]');
    this.singlePriceElement = this.container.querySelector('[data-single-price]');
    this.singleComparePriceElement = this.container.querySelector('[data-single-compare-price]');
    this.duoPriceElement = this.container.querySelector('[data-duo-price]');
    this.duoComparePriceElement = this.container.querySelector('[data-duo-compare-price]');
    this.trioPriceElement = this.container.querySelector('[data-trio-price]');
    this.trioComparePriceElement = this.container.querySelector('[data-trio-compare-price]');
    this.savingsBadgeElements = {
      single: this.container.querySelector('[data-savings-badge="single"]'),
      dual: this.container.querySelector('[data-savings-badge="dual"]'),
      trio: this.container.querySelector('[data-savings-badge="trio"]')
    };
    this.savingsValueElements = {
      single: this.container.querySelector('[data-savings-value="single"]'),
      dual: this.container.querySelector('[data-savings-value="dual"]'),
      trio: this.container.querySelector('[data-savings-value="trio"]')
    };
    this.singleSavingsAmountElement = this.container.querySelector('[data-single-savings-amount]');
    this.singleSavingsPercentElement = this.container.querySelector('[data-single-savings-percent]');

    this.#init();
  }

  #init() {
    this.#disableThemeVariantPicker();
    this.#syncFromInitialVariant();
    this.#observeReploFormVariant();
    this.#underlineGratisInGiftPills();
    this.#attachEventListeners();
    this.#updatePricing();
    this.#handleOptionChange();
    this.#updateSummary();
    this.#registerIntelligemsLifecycleListeners();
    this.#scheduleIntelligemsRefresh();
    this.#overrideAddToCart();
  }

  #underlineGratisInGiftPills() {
    this.container.querySelectorAll('.single-dual-bundle__gift-pill').forEach((el) => {
      if (el.innerHTML && el.innerHTML.includes('Gratis')) {
        el.innerHTML = this.#underlineGratisMarkup(el.innerHTML);
      }
    });

    this.container.querySelectorAll('[data-gratis-copy]').forEach((el) => {
      const rawText = el.getAttribute('data-raw-text') || el.textContent || '';
      this.#renderGratisCopy(el, rawText);
    });
  }

  #escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #formatGratisCopy(rawText) {
    return this.#escapeHtml(rawText).replace(/Gratis/g, '<span class="single-dual-bundle__gratis-underlined">Gratis</span>');
  }

  #underlineGratisMarkup(rawMarkup) {
    return String(rawMarkup ?? '').replace(/Gratis/g, '<span class="single-dual-bundle__gratis-underlined">Gratis</span>');
  }

  #renderGratisCopy(element, rawText) {
    if (!element) return;

    element.setAttribute('data-raw-text', rawText || '');
    element.innerHTML = this.#formatGratisCopy(rawText || '');
  }

  #syncFromInitialVariant() {
    const variantId = this.#getInitialVariantId();
    if (!variantId) return;

    if (this.bundleVariant1Input) {
      this.bundleVariant1Input.value = variantId;
    }

    const radio2 = this.container.querySelector(`[data-variant-radio="2"][value="${variantId}"]`);
    const radio3 = this.container.querySelector(`[data-variant-radio="3"][value="${variantId}"]`);
    const radio4 = this.container.querySelector(`[data-variant-radio="4"][value="${variantId}"]`);
    if (radio2) radio2.checked = true;
    if (radio3) radio3.checked = true;
    if (radio4) radio4.checked = true;

    this.#updateVariantInputs();
    this.#updatePricing();
    this.#updateSummary();
  }

  #observeReploFormVariant() {
    const form = this.#getProductForm();
    const idInput = form?.querySelector('input[name="id"]');
    if (!idInput) return;

    let pollTimer = null;
    let pollAttempts = 0;
    const maxPollAttempts = 60;

    const syncFromForm = (variantIdOverride = null) => {
      const variantId = String(variantIdOverride || idInput.value || '');
      if (!variantId) return false;

      if (this.bundleVariant1Input) {
        this.bundleVariant1Input.value = variantId;
      }

      this.#updatePricing(variantId);
      const option = this.#getSelectedOption();
      if (option === 'single') {
        this.#updateSummary();
      } else if (option === 'dual' || option === 'trio') {
        this.#syncVariantRadiosFromForm(true);
      }

      return true;
    };

    const startPolling = (resetAttempts = false) => {
      if (resetAttempts) {
        pollAttempts = 0;
      }

      if (pollTimer != null) {
        return;
      }

      const poll = () => {
        pollTimer = null;

        if (!document.body.contains(this.container)) {
          return;
        }

        syncFromForm();

        const formVariantId = String(idInput.value || '');
        if (formVariantId && this.bundleVariant1Input?.value === formVariantId) {
          return;
        }

        pollAttempts += 1;
        if (pollAttempts >= maxPollAttempts) {
          return;
        }

        pollTimer = window.setTimeout(poll, 150);
      };

      pollTimer = window.setTimeout(poll, 0);
    };

    const handleVariantChanged = (event) => {
      const eventVariantId = event?.detail?.variant?.id;
      if (!eventVariantId) return;

      const normalizedVariantId = String(eventVariantId);
      if (idInput.value !== normalizedVariantId) {
        idInput.value = normalizedVariantId;
      }

      syncFromForm(normalizedVariantId);
      startPolling(true);
    };

    syncFromForm();
    idInput.addEventListener('change', () => syncFromForm());
    idInput.addEventListener('input', () => syncFromForm());
    form.addEventListener('variant:changed', handleVariantChanged);
    document.addEventListener('variant:changed', handleVariantChanged);

    const observer = new MutationObserver(() => syncFromForm());
    observer.observe(idInput, { attributes: true, attributeFilter: ['value'] });

    startPolling(true);
    window.addEventListener('load', () => startPolling(true));
    window.addEventListener('pageshow', () => startPolling(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        startPolling(true);
      }
    });
  }

  #getProductForm() {
    const productForm = this.container.closest('.product-form');
    const reploContainer = this.container.closest('[data-replo-product-container]');
    return this.container.closest('form') || productForm?.querySelector('form[is="product-form"]') || productForm?.querySelector('form') || reploContainer?.querySelector('form') || document.querySelector('form[action*="/cart/add"]');
  }

  #syncBundleVariant1FromForm() {
    const variantId = this.#getInitialVariantId();
    if (variantId && this.bundleVariant1Input) {
      this.bundleVariant1Input.value = variantId;
    }
  }

  #getInitialVariantId() {
    // When in Single mode, type cards (City, Trekking, MTB) are the source of truth - user clicks them directly
    const typeRadio = this.container.querySelector('[data-type-radio="1"]:checked');
    if (typeRadio?.value) {
      return typeRadio.value;
    }
    const form = this.#getProductForm();
    const formVariantInput = form?.querySelector('input[name="id"]');
    if (formVariantInput?.value) {
      return formVariantInput.value;
    }
    return this.config?.selectedVariantId || null;
  }

  #disableThemeVariantPicker() {
    const productVariants = this.container.closest('.product-form')?.querySelector('product-variants');
    if (productVariants) {
      productVariants.style.display = 'none';
      const variantInputs = productVariants.querySelectorAll('input[type="radio"]');
      for (const input of variantInputs) {
        input.disabled = true;
      }
    }
    // Do NOT disable input[name="id"] - Replo's built-in variant selector needs to update it
  }

  #loadConfig() {
    const configElement = this.container.querySelector('[data-bundle-config]');
    if (!configElement) return null;
    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Single/Dual Bundle: Failed to parse config', error);
      return null;
    }
  }

  #attachEventListeners() {
    for (const radio of this.optionRadios) {
      radio.addEventListener('change', () => this.#handleOptionChange(), true);
    }

    this.container.addEventListener('change', (e) => {
      if (e.target?.matches?.('[data-type-radio="1"]')) {
        this.#handleTypeCardChange();
      }
      if (e.target?.matches?.('[data-variant-radio]')) {
        this.#handleVariantRadioChange(e);
      }
    });

    this.container.addEventListener('click', (e) => e.stopPropagation(), true);

    const labels = this.container.querySelectorAll('[tabindex="0"]');
    for (const label of labels) {
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          label.click();
        }
      });
    }
  }

  #getSelectedOption() {
    const selected = this.container.querySelector('[data-option-radio]:checked');
    return selected ? selected.value : 'single';
  }

  #getVariantIdFromRadio(radioNum) {
    const radio = this.container.querySelector(`[data-variant-radio="${radioNum}"]:checked`);
    return radio ? radio.value : null;
  }

  #getVariantTitleFromRadio(radioNum) {
    const radio = this.container.querySelector(`[data-variant-radio="${radioNum}"]:checked`);
    return radio ? radio.getAttribute('data-variant-title') : '';
  }

  #handleOptionChange() {
    const option = this.#getSelectedOption();

    for (const card of this.optionCards) {
      const cardOption = card.getAttribute('data-option-card');
      if (cardOption === option) {
        card.classList.add('single-dual-bundle__option-card--selected');
      } else {
        card.classList.remove('single-dual-bundle__option-card--selected');
      }
    }

    if (this.optionInput) {
      this.optionInput.value = option;
    }

    if (this.optionItemDual) {
      this.optionItemDual.classList.toggle('single-dual-bundle__option-item--expanded', option === 'dual');
    }
    if (this.optionItemTrio) {
      this.optionItemTrio.classList.toggle('single-dual-bundle__option-item--expanded', option === 'trio');
    }

    if (option === 'single') {
      const currentSlot = this.bundleExpandable?.parentElement;
      if (currentSlot?.matches('[data-bundle-expandable-slot]')) {
        currentSlot.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
        const expandable = this.bundleExpandable;
        const storage = this.expandableStorage;
        setTimeout(() => {
          if (expandable && storage && expandable.parentElement?.matches('[data-bundle-expandable-slot]')) {
            storage.appendChild(expandable);
            expandable.classList.add('single-dual-bundle__bundle-expandable--hidden');
          }
        }, 400);
      }
      this.#updateBundleGiftFooter();
      this.#syncBundleVariant1FromForm();
      this.#updatePricing();
      this.#updateSummary();
    } else {
      const targetSlot = option === 'trio' ? this.expandableSlotTrio : this.expandableSlotDual;
      const didMove = this.bundleExpandable && targetSlot && this.bundleExpandable.parentElement !== targetSlot;

      if (didMove) {
        const prevSlot = this.bundleExpandable.parentElement;
        if (prevSlot?.matches('[data-bundle-expandable-slot]')) {
          prevSlot.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
        }
        targetSlot.appendChild(this.bundleExpandable);
      }
      if (this.bundleExpandable) {
        this.bundleExpandable.classList.remove('single-dual-bundle__bundle-expandable--hidden');
      }
      if (this.expandableSlotDual !== targetSlot && this.expandableSlotDual) {
        this.expandableSlotDual.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
      }
      if (this.expandableSlotTrio !== targetSlot && this.expandableSlotTrio) {
        this.expandableSlotTrio.classList.remove('single-dual-bundle__bundle-expandable-slot--expanded');
      }

      if (targetSlot) {
        if (didMove) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              targetSlot.classList.add('single-dual-bundle__bundle-expandable-slot--expanded');
            });
          });
        } else {
          targetSlot.classList.add('single-dual-bundle__bundle-expandable-slot--expanded');
        }
      }

      if (this.bundleInfoText) {
        const rawText = option === 'trio'
          ? (this.config?.trioInfoText || 'Wähle drei Varianten für dein Trio-Set')
          : (this.config?.duoInfoText || 'Wähle zwei Varianten für dein Duo-Set');
        this.bundleInfoText.innerHTML = this.#formatGratisCopy(rawText);
      }

      this.#updateBundleGiftFooter();

      if (this.variantGroup3) {
        this.variantGroup3.style.display = option === 'trio' ? '' : 'none';
      }

      if (option === 'dual') {
        this.#syncVariantRadiosFromForm();
      }

      this.#updateVariantInputs();
      this.#updatePricing();
      this.#updateSummary();
      this.#updateImageGalleryForVariant();
    }
  }

  #updateBundleGiftFooter() {
    if (!this.bundleGiftFooter || !this.bundleGiftFooterText) return;

    const option = this.#getSelectedOption();
    if (option === 'single') {
      this.bundleGiftFooter.hidden = true;
      this.bundleGiftFooterText.innerHTML = '';
      return;
    }

    const shouldShow = option === 'trio'
      ? Boolean(this.config?.showTrioGiftCopy && this.config?.trioGiftFooterText)
      : Boolean(this.config?.showDuoGiftCopy && this.config?.duoGiftFooterText);
    const rawText = option === 'trio'
      ? (this.config?.trioGiftFooterText || '')
      : (this.config?.duoGiftFooterText || '');

    this.bundleGiftFooter.hidden = !shouldShow;

    if (!shouldShow) {
      this.bundleGiftFooterText.innerHTML = '';
      return;
    }

    this.#renderGratisCopy(this.bundleGiftFooterText, rawText);
  }

  #syncVariantRadiosFromForm(onlySattel1 = false) {
    const variantId = this.#getInitialVariantId();
    if (!variantId) return;

    const radio2 = this.container.querySelector(`[data-variant-radio="2"][value="${variantId}"]`);
    if (radio2) radio2.checked = true;

    if (!onlySattel1) {
      const radio3 = this.container.querySelector(`[data-variant-radio="3"][value="${variantId}"]`);
      const radio4 = this.container.querySelector(`[data-variant-radio="4"][value="${variantId}"]`);
      if (radio3) radio3.checked = true;
      if (radio4) radio4.checked = true;
    }

    this.#updateVariantInputs();
  }

  #getDisplayVariantId() {
    const option = this.#getSelectedOption();
    if (option === 'single') {
      return this.#getInitialVariantId();
    }
    return this.#getVariantIdFromRadio(2);
  }

  #updateImageGalleryForVariant(variantIdOverride) {
    const variantId = variantIdOverride ?? this.#getDisplayVariantId();
    if (!variantId || !this.config?.variants) return;

    const variant = this.config.variants.find(v => v.id.toString() === variantId.toString());
    if (!variant) return;

    const variantForDispatch = { ...variant };
    if (!variantForDispatch.featured_media && (variant.featured_image || variant.image)) {
      variantForDispatch.featured_media = variant.featured_image || variant.image;
    }

    const form = this.#getProductForm();
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
        idInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      form.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: { variant: variantForDispatch }
      }));
    }
    document.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: { variant: variantForDispatch }
    }));

    this.#syncReploVariant(variantId, variant);
    this.#scrollToVariantImage(variant);
  }

  #syncReploVariant(variantId, variant) {
    if (!variantId) return;
    const idStr = String(variantId);
    const reploContainer = this.container.closest('[data-replo-product-container]');
    const scope = reploContainer ?? document;

    let reploEl = scope.querySelector(`[data-replo-set-active-variant="${idStr}"]`);
    if (!reploEl) {
      reploEl = document.querySelector(`[data-replo-set-active-variant="${idStr}"]`);
    }

    if (!reploEl && variant && this.config?.optionIndex != null) {
      const optionValue = variant.options?.[this.config.optionIndex];
      if (optionValue) {
        const allWithOption = document.querySelectorAll('[data-replo-set-active-option]');
        for (const el of allWithOption) {
          if (el.getAttribute('data-replo-set-active-option') === optionValue) {
            reploEl = el;
            break;
          }
        }
      }
    }

    if (!reploEl) {
      const allWithVariantAttr = document.querySelectorAll('[data-replo-set-active-variant]');
      for (const el of allWithVariantAttr) {
        if (el.getAttribute('data-replo-set-active-variant') === idStr) {
          reploEl = el;
          break;
        }
      }
    }

    if (!reploEl) {
      const options = document.querySelectorAll('[role="option"]');
      for (const opt of options) {
        const withVariant = opt.querySelector(`[data-replo-set-active-variant="${idStr}"]`) || opt.closest(`[data-replo-set-active-variant="${idStr}"]`);
        if (withVariant) {
          reploEl = withVariant;
          break;
        }
        if (opt.getAttribute('data-replo-set-active-variant') === idStr) {
          reploEl = opt;
          break;
        }
      }
    }

    if (!reploEl && this.config?.variants) {
      const variantIndex = this.config.variants.findIndex(v => String(v.id) === idStr);
      if (variantIndex >= 0) {
        const listbox = reploContainer?.querySelector('[role="listbox"]') ?? document.querySelector('[role="listbox"]');
        const options = listbox ? listbox.querySelectorAll('[role="option"]') : document.querySelectorAll('[role="option"]');
        if (options[variantIndex]) {
          reploEl = options[variantIndex];
        }
      }
    }

    if (reploEl) {
      reploEl.click();
    }
  }

  #scrollToVariantImage(variant) {
    if (!variant) return;

    let mediaId = null;
    if (variant.featured_media?.id) {
      mediaId = variant.featured_media.id;
    } else if (variant.image?.id) {
      mediaId = variant.image.id;
    } else if (variant.featured_image?.id) {
      mediaId = variant.featured_image.id;
    }
    if (!mediaId) return;

    const swiperEl = document.querySelector('[id*="-media-list"][data-transition-effect]');
    if (!swiperEl?.swiper) {
      setTimeout(() => this.#scrollToVariantImage(variant), 100);
      return;
    }

    const targetSlide = swiperEl.querySelector(`.swiper-slide[data-media-id="${mediaId}"]:not(.is-filtered)`);
    if (targetSlide) {
      const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide:not(.is-filtered)'));
      const slideIndex = slides.indexOf(targetSlide);
      if (slideIndex >= 0) {
        swiperEl.swiper.slideTo(slideIndex);
      }
    }
  }

  #syncVariantRadiosFromMain() {
    const selectedRadio = this.container.querySelector('[data-type-radio="1"]:checked');
    if (!selectedRadio) return;

    const variantId = selectedRadio.value;
    const radio2 = this.container.querySelector('[data-variant-radio="2"]');
    const radio3 = this.container.querySelector('[data-variant-radio="3"]');
    const radio4 = this.container.querySelector('[data-variant-radio="4"]');

    if (radio2) {
      const match2 = this.container.querySelector(`[data-variant-radio="2"][value="${variantId}"]`);
      if (match2) match2.checked = true;
    }
    if (radio3) {
      const match3 = this.container.querySelector(`[data-variant-radio="3"][value="${variantId}"]`);
      if (match3) match3.checked = true;
    }
    if (radio4) {
      const match4 = this.container.querySelector(`[data-variant-radio="4"][value="${variantId}"]`);
      if (match4) match4.checked = true;
    }

    this.#updateVariantInputs();
  }

  #handleTypeCardChange() {
    if (this.#getSelectedOption() !== 'single') return;
    const typeRadio = this.container.querySelector('[data-type-radio="1"]:checked');
    if (!typeRadio?.value) return;

    const variantId = typeRadio.value;

    // Sync form's input[name="id"] so Replo gallery and other form-dependent UI stay in sync
    const form = this.#getProductForm();
    const idInput = form?.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variantId;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      idInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if (this.bundleVariant1Input) {
      this.bundleVariant1Input.value = variantId;
    }

    this.#updatePricing(variantId);
    this.#syncReploVariant(variantId);
    this.#updateSummary();
    this.#updateImageGalleryForVariant();
  }

  #updatePricing(variantId = this.#getInitialVariantId()) {
    const singlePricingState = this.#updateSingleCardPricing(variantId);
    const duoPricingState = this.#resolveBundlePricing(singlePricingState, 2);
    const trioPricingState = this.#resolveBundlePricing(singlePricingState, 3);

    this.#applyBundlePricing('dual', duoPricingState);
    this.#applyBundlePricing('trio', trioPricingState);
    this.#publishPricingState(singlePricingState, duoPricingState, trioPricingState);

    return singlePricingState;
  }

  #updateSingleCardPricing(variantId = this.#getInitialVariantId()) {
    const pricingState = this.#resolveSingleCardPricing(variantId);
    this.#syncIntelligemsRefreshState(pricingState);

    if (this.singlePriceElement) {
      this.singlePriceElement.textContent = this.#formatMoney(pricingState.priceCents);
      this.#updatePricingElementMetadata(this.singlePriceElement, pricingState);
    }

    if (this.singleComparePriceElement) {
      this.#updatePricingElementMetadata(this.singleComparePriceElement, pricingState);
      if (pricingState.comparePriceCents > 0) {
        this.singleComparePriceElement.textContent = this.#formatMoney(pricingState.comparePriceCents);
        this.singleComparePriceElement.style.display = '';
      } else {
        this.singleComparePriceElement.style.display = 'none';
      }
    }

    if (this.singleSavingsAmountElement) {
      this.singleSavingsAmountElement.textContent = this.#formatMoney(pricingState.savingsCents);
      this.#updatePricingElementMetadata(this.singleSavingsAmountElement, pricingState);
    }

    if (this.singleSavingsPercentElement) {
      const percentValue = pricingState.usingIntelligems
        ? pricingState.savingsPercent
        : Number(this.config?.singleDiscountBadge || 0);
      this.singleSavingsPercentElement.textContent = String(percentValue);
      this.#updatePricingElementMetadata(this.singleSavingsPercentElement, pricingState);
    }

    if (this.savingsBadgeElements.single) {
      const showSavings = pricingState.usingIntelligems
        ? pricingState.savingsCents > 0 && pricingState.savingsPercent > 0
        : Number(this.config?.singleDiscountBadge || 0) > 0;
      this.savingsBadgeElements.single.style.display = showSavings ? '' : 'none';
    }

    return pricingState;
  }

  #resolveSingleCardPricing(variantId) {
    const variant = this.#findVariantById(variantId);
    const pricingState = {
      productId: String(this.config?.productId || ''),
      variantId: String(variantId || this.config?.selectedVariantId || ''),
      priceCents: Number(this.config?.singlePrice || 0),
      comparePriceCents: Number(this.config?.singleComparePrice || 0),
      savingsCents: Math.max(Number(this.config?.singleComparePrice || 0) - Number(this.config?.singlePrice || 0), 0),
      savingsPercent: Number(this.config?.singleDiscountBadge || 0),
      usingIntelligems: false,
      usingVariantPriceFallback: false
    };

    const variantPriceCents = this.#getVariantPriceCents(variant);
    const variantComparePriceCents = this.#getVariantComparePriceCents(variant);

    if (pricingState.priceCents <= 0 && variantPriceCents > 0) {
      pricingState.priceCents = variantPriceCents;
      pricingState.comparePriceCents = variantComparePriceCents;
      pricingState.savingsCents = Math.max(pricingState.comparePriceCents - pricingState.priceCents, 0);
      pricingState.savingsPercent = pricingState.comparePriceCents > pricingState.priceCents
        ? Math.round((pricingState.savingsCents * 100) / pricingState.comparePriceCents)
        : 0;
      pricingState.usingVariantPriceFallback = true;
    }

    const intelligemsPriceCents = this.#getIntelligemsPriceCents(pricingState.variantId);
    if (intelligemsPriceCents == null) {
      return pricingState;
    }

    const intelligemsComparePriceCents = this.#getIntelligemsComparePriceCents(pricingState.variantId);
    pricingState.priceCents = intelligemsPriceCents;
    pricingState.comparePriceCents = intelligemsComparePriceCents ?? 0;
    pricingState.savingsCents = Math.max(pricingState.comparePriceCents - pricingState.priceCents, 0);
    pricingState.savingsPercent = pricingState.comparePriceCents > pricingState.priceCents
      ? Math.round((pricingState.savingsCents * 100) / pricingState.comparePriceCents)
      : 0;
    pricingState.usingIntelligems = true;
    pricingState.usingVariantPriceFallback = false;

    return pricingState;
  }

  #findVariantById(variantId) {
    if (!variantId) return null;
    return this.config?.variants?.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  #getVariantPriceCents(variant) {
    const priceCents = Number(variant?.price);
    return Number.isFinite(priceCents) && priceCents > 0 ? priceCents : 0;
  }

  #getVariantComparePriceCents(variant) {
    const comparePriceCents = Number(variant?.compare_at_price);
    return Number.isFinite(comparePriceCents) && comparePriceCents > 0 ? comparePriceCents : 0;
  }

  #getIntelligemsPriceCents(variantId) {
    const priceApi = window.igData?.price;
    if (!priceApi?.getPriceByVariantId || !variantId) return null;

    try {
      return this.#parsePriceStringToCents(priceApi.getPriceByVariantId(String(variantId)));
    } catch (error) {
      console.warn('Single/Dual Bundle: Failed to read IntelliGems price', error);
      return null;
    }
  }

  #getIntelligemsComparePriceCents(variantId) {
    const priceApi = window.igData?.price;
    if (!priceApi?.getComparePriceByVariantId || !variantId) return null;

    try {
      return this.#parsePriceStringToCents(priceApi.getComparePriceByVariantId(String(variantId)));
    } catch (error) {
      console.warn('Single/Dual Bundle: Failed to read IntelliGems compare price', error);
      return null;
    }
  }

  #hasIntelligemsPriceApi() {
    return Boolean(
      window.igData?.price?.getPriceByVariantId
      && window.igData?.price?.getComparePriceByVariantId
    );
  }

  #hasResolvedIntelligemsPricing(variantId, { expectsComparePrice = false } = {}) {
    const priceCents = this.#getIntelligemsPriceCents(variantId);
    if (!(Number.isFinite(Number(priceCents)) && Number(priceCents) > 0)) {
      return false;
    }

    if (!expectsComparePrice) {
      return true;
    }

    return this.#getIntelligemsComparePriceCents(variantId) != null;
  }

  #syncIntelligemsRefreshState(pricingState) {
    if (this.isIntelligemsRefreshTicking) return;

    const variantId = String(pricingState?.variantId || '');
    const expectsComparePrice = Number(this.config?.singleComparePrice || 0) > 0;
    const hasResolvedPrice = pricingState?.usingIntelligems
      && this.#hasResolvedIntelligemsPricing(variantId, { expectsComparePrice });

    if (hasResolvedPrice) {
      this.#clearIntelligemsRefreshTimer();
      return;
    }

    if (variantId) {
      this.#scheduleIntelligemsRefresh(variantId);
    }
  }

  #refreshForIntelligemsReady() {
    this.#updatePricing();
    this.#updateSummary();
  }

  #clearIntelligemsRefreshTimer() {
    if (this.intelligemsRefreshTimer != null) {
      window.clearTimeout(this.intelligemsRefreshTimer);
      this.intelligemsRefreshTimer = null;
    }
  }

  #registerIntelligemsLifecycleListeners() {
    window.addEventListener('load', () => {
      this.#scheduleIntelligemsRefresh(null, { resetAttempts: true });
    });

    window.addEventListener('pageshow', () => {
      this.#scheduleIntelligemsRefresh(null, { resetAttempts: true });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.#scheduleIntelligemsRefresh(null, { resetAttempts: true });
      }
    });
  }

  #scheduleIntelligemsRefresh(variantId = null, { resetAttempts = false } = {}) {
    const activeVariantId = String(variantId || this.#getInitialVariantId() || '');
    if (!activeVariantId) return;

    if (resetAttempts || this.intelligemsRefreshVariantId !== activeVariantId) {
      this.intelligemsRefreshAttempts = 0;
      this.intelligemsRefreshVariantId = activeVariantId;
    }

    if (this.intelligemsRefreshTimer != null) {
      return;
    }

    const maxAttempts = 60;

    const tryRefresh = () => {
      this.intelligemsRefreshTimer = null;

      if (!document.body.contains(this.container)) {
        this.#clearIntelligemsRefreshTimer();
        return;
      }

      const nextVariantId = String(this.#getInitialVariantId() || this.intelligemsRefreshVariantId || '');
      if (!nextVariantId) {
        return;
      }

      if (this.intelligemsRefreshVariantId !== nextVariantId) {
        this.intelligemsRefreshVariantId = nextVariantId;
        this.intelligemsRefreshAttempts = 0;
      }

      const expectsComparePrice = Number(this.config?.singleComparePrice || 0) > 0;

      if (this.#hasResolvedIntelligemsPricing(this.intelligemsRefreshVariantId, { expectsComparePrice })) {
        this.isIntelligemsRefreshTicking = true;
        try {
          this.#refreshForIntelligemsReady();
        } finally {
          this.isIntelligemsRefreshTicking = false;
        }
        this.#clearIntelligemsRefreshTimer();
        return;
      }

      this.intelligemsRefreshAttempts += 1;
      if (this.intelligemsRefreshAttempts >= maxAttempts) {
        this.#clearIntelligemsRefreshTimer();
        return;
      }

      this.isIntelligemsRefreshTicking = true;
      try {
        this.#refreshForIntelligemsReady();
      } finally {
        this.isIntelligemsRefreshTicking = false;
      }

      this.intelligemsRefreshTimer = window.setTimeout(tryRefresh, 150);
    };

    this.intelligemsRefreshTimer = window.setTimeout(tryRefresh, 0);
  }

  #parsePriceStringToCents(value) {
    if (value == null || value === '') return null;

    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.round(value * 100);
    }

    let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
    if (!normalizedValue) return null;

    const hasComma = normalizedValue.includes(',');
    const hasDot = normalizedValue.includes('.');

    if (hasComma && hasDot) {
      if (normalizedValue.lastIndexOf(',') > normalizedValue.lastIndexOf('.')) {
        normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
      } else {
        normalizedValue = normalizedValue.replace(/,/g, '');
      }
    } else if (hasComma) {
      normalizedValue = normalizedValue.replace(',', '.');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : null;
  }

  #updatePricingElementMetadata(element, pricingState) {
    if (!element) return;

    if (pricingState.productId) {
      element.setAttribute('data-product-id', pricingState.productId);
    }

    if (pricingState.variantId) {
      element.setAttribute('data-variant-id', pricingState.variantId);
    }
  }

  #updateSavingsBadge(key, value) {
    const badgeElement = this.savingsBadgeElements[key];
    const valueElement = this.savingsValueElements[key];
    if (!badgeElement || !valueElement) return;

    if (Number(value) > 0) {
      badgeElement.style.display = '';
      valueElement.textContent = String(value);
    } else {
      badgeElement.style.display = 'none';
    }
  }

  #publishPricingState(singlePricingState, duoPricingState, trioPricingState) {
    if (!this.container) return;

    const pricingStates = {
      single: {
        productId: singlePricingState?.productId || '',
        variantId: singlePricingState?.variantId || '',
        totalPriceCents: Number(singlePricingState?.priceCents || 0),
        totalComparePriceCents: Number(singlePricingState?.comparePriceCents || 0),
        pricePerItemCents: Number(singlePricingState?.priceCents || 0),
        comparePricePerItemCents: Number(singlePricingState?.comparePriceCents || 0),
        savingsPercent: Number(singlePricingState?.savingsPercent || 0)
      },
      dual: {
        productId: duoPricingState?.productId || '',
        variantId: duoPricingState?.variantId || '',
        totalPriceCents: Number(duoPricingState?.totalPriceCents || 0),
        totalComparePriceCents: Number(duoPricingState?.totalComparePriceCents || 0),
        pricePerItemCents: Number(duoPricingState?.pricePerItemCents || 0),
        comparePricePerItemCents: Number(duoPricingState?.comparePricePerItemCents || 0),
        savingsPercent: Number(duoPricingState?.savingsPercent || 0)
      },
      trio: {
        productId: trioPricingState?.productId || '',
        variantId: trioPricingState?.variantId || '',
        totalPriceCents: Number(trioPricingState?.totalPriceCents || 0),
        totalComparePriceCents: Number(trioPricingState?.totalComparePriceCents || 0),
        pricePerItemCents: Number(trioPricingState?.pricePerItemCents || 0),
        comparePricePerItemCents: Number(trioPricingState?.comparePricePerItemCents || 0),
        savingsPercent: Number(trioPricingState?.savingsPercent || 0)
      }
    };

    Object.entries(pricingStates).forEach(([key, pricingState]) => {
      this.container.setAttribute(`data-pricing-${key}-product-id`, String(pricingState.productId || ''));
      this.container.setAttribute(`data-pricing-${key}-variant-id`, String(pricingState.variantId || ''));
      this.container.setAttribute(`data-pricing-${key}-total-price-cents`, String(pricingState.totalPriceCents));
      this.container.setAttribute(`data-pricing-${key}-total-compare-price-cents`, String(pricingState.totalComparePriceCents));
      this.container.setAttribute(`data-pricing-${key}-price-per-item-cents`, String(pricingState.pricePerItemCents));
      this.container.setAttribute(`data-pricing-${key}-compare-price-per-item-cents`, String(pricingState.comparePricePerItemCents));
      this.container.setAttribute(`data-pricing-${key}-savings-percent`, String(pricingState.savingsPercent));
    });

    this.container.dispatchEvent(new CustomEvent('single-dual-bundle:pricing-updated', {
      bubbles: true,
      detail: pricingStates
    }));
  }

  #applyBundlePricing(key, pricingState) {
    const priceElement = key === 'dual' ? this.duoPriceElement : this.trioPriceElement;
    const compareElement = key === 'dual' ? this.duoComparePriceElement : this.trioComparePriceElement;

    if (priceElement) {
      priceElement.textContent = this.#formatMoney(pricingState.pricePerItemCents);
      this.#updatePricingElementMetadata(priceElement, pricingState);
    }

    if (compareElement) {
      this.#updatePricingElementMetadata(compareElement, pricingState);
      if (pricingState.comparePricePerItemCents > 0) {
        compareElement.textContent = this.#formatMoney(pricingState.comparePricePerItemCents);
        compareElement.style.display = '';
      } else {
        compareElement.style.display = 'none';
      }
    }

    this.#updateSavingsBadge(key, pricingState.savingsPercent);
  }

  #resolveBundlePricing(singlePricingState, quantity) {
    const bundleKey = quantity === 2 ? 'duo' : 'trio';
    const totalPriceCents = Number(this.config?.[`${bundleKey}Price`] || 0);
    const totalComparePriceCents = Number(this.config?.[`${bundleKey}ComparePrice`] || 0);
    const fallbackDiscountBadge = Number(this.config?.[`${bundleKey}DiscountBadge`] || 0);
    const testingDiscountCents = Number(this.config?.[quantity === 2 ? 'testingDuoDiscount' : 'testingTrioDiscount'] || 0);
    const pricingState = {
      productId: String(singlePricingState?.productId || this.config?.productId || ''),
      variantId: String(singlePricingState?.variantId || this.config?.selectedVariantId || ''),
      totalPriceCents,
      totalComparePriceCents,
      pricePerItemCents: Math.round(totalPriceCents / quantity),
      comparePricePerItemCents: Math.round(totalComparePriceCents / quantity),
      savingsPercent: fallbackDiscountBadge,
      usingPriceTesting: false
    };

    const canUseAutomaticPricing = Boolean(
      this.config?.enablePriceTestingMode
      && Number(singlePricingState?.priceCents || 0) > 0
      && (singlePricingState?.usingIntelligems || singlePricingState?.usingVariantPriceFallback)
    );

    if (!canUseAutomaticPricing) {
      return pricingState;
    }

    const liveBundleTotalCents = Math.max((Number(singlePricingState.priceCents || 0) * quantity) - testingDiscountCents, 0);
    const liveBundleCompareTotalCents = Math.max(Number(singlePricingState.comparePriceCents || 0) * quantity, 0);
    const savingsCents = Math.max(liveBundleCompareTotalCents - liveBundleTotalCents, 0);

    pricingState.totalPriceCents = liveBundleTotalCents;
    pricingState.totalComparePriceCents = liveBundleCompareTotalCents;
    pricingState.pricePerItemCents = Math.round(liveBundleTotalCents / quantity);
    pricingState.comparePricePerItemCents = Math.round(liveBundleCompareTotalCents / quantity);
    pricingState.savingsPercent = liveBundleCompareTotalCents > liveBundleTotalCents
      ? Math.round((savingsCents * 100) / liveBundleCompareTotalCents)
      : 0;
    pricingState.usingPriceTesting = Boolean(singlePricingState?.usingIntelligems);

    return pricingState;
  }

  #handleVariantRadioChange(e) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    const changedRadio = e?.target?.getAttribute?.('data-variant-radio');
    const isSattel1 = changedRadio === '2';

    try {
      this.#updateVariantInputs();
      const pricingVariantId = this.#getVariantIdFromRadio(2) || this.#getInitialVariantId();
      this.#updatePricing(pricingVariantId);
      this.#updateSummary();
      if (isSattel1) {
        const variantId = e?.target?.value ?? null;
        this.#updateImageGalleryForVariant(variantId);
      }
    } finally {
      setTimeout(() => { this.isUpdating = false; }, 10);
    }
  }

  #updateVariantInputs() {
    const v2 = this.#getVariantIdFromRadio(2);
    const v3 = this.#getVariantIdFromRadio(3);
    const v4 = this.#getVariantIdFromRadio(4);

    if (this.bundleVariant1Input && v2) {
      this.bundleVariant1Input.value = v2;
    }
    if (this.bundleVariant2Input && v3) {
      this.bundleVariant2Input.value = v3;
    }
    if (this.bundleVariant3Input && v4) {
      this.bundleVariant3Input.value = v4;
    }
  }

  #updateSummary() {
    if (!this.summaryText) return;

    const option = this.#getSelectedOption();

    if (option === 'single') {
      const variantId = this.#getInitialVariantId();
      const variant = this.config?.variants?.find(v => v.id.toString() === variantId?.toString());
      const title = variant?.options?.[this.config?.optionIndex ?? 0]?.split(' - ')[0] ?? '';
      this.summaryText.textContent = `1x ${title}`;
    } else if (option === 'dual') {
      const t2 = this.#getVariantTitleFromRadio(2);
      const t3 = this.#getVariantTitleFromRadio(3);
      this.summaryText.textContent = `1x ${t2} + 1x ${t3}`;
    } else if (option === 'trio') {
      const t2 = this.#getVariantTitleFromRadio(2);
      const t3 = this.#getVariantTitleFromRadio(3);
      const t4 = this.#getVariantTitleFromRadio(4);
      this.summaryText.textContent = `1x ${t2} + 1x ${t3} + 1x ${t4}`;
    }
  }

  #overrideAddToCart() {
    const reploContainer = this.container.closest('[data-replo-product-container]');
    const reploAddBtn = reploContainer?.querySelector('[data-replo-add-product-variant-to-cart]');

    // Replo uses a custom add button that does NOT submit the form - it adds the variant from input[name="id"] directly.
    // We must intercept that button click so our bundle logic (dual/trio variants) is used instead.
    if (reploAddBtn) {
      reploAddBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.#handleAddToCart(reploAddBtn);
        return false;
      }, true);
    }

    let productForm = this.container.closest('form');
    if (!productForm) {
      productForm = this.container.closest('.product-form')?.querySelector('form[is="product-form"]');
    }
    if (!productForm) {
      productForm = reploContainer?.querySelector('form');
    }
    if (!productForm) {
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.#handleAddToCart();
        return false;
      }, true);
    }
  }

  async #handleAddToCart(addToCartButton) {
    if (this.isAddingToCart) {
      return;
    }
    this.isAddingToCart = true;

    const option = this.#getSelectedOption();
    const btn = addToCartButton ?? this.container.closest('[data-replo-product-container]')?.querySelector('[data-replo-add-product-variant-to-cart]') ?? document.querySelector('.product-form__add-button');
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    try {
      const items = this.#buildAddToCartItems(option);
      if (!items.length) {
        throw new Error(option === 'single' ? 'No variant selected' : 'Please select all variants');
      }

      const cartData = await this.#addToCartMultiAPI(items);
      console.log('Single/Dual Bundle: Successfully added to cart');

      // Open cart with items immediately; BOGOS discount/gift applies afterward
      this.#refreshCart(cartData);
    } catch (error) {
      console.error('Single/Dual Bundle: Error adding to cart', error);
      this.#showErrorMessage(error.message);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.setAttribute('aria-busy', 'false');
      }
      this.isAddingToCart = false;
    }
  }

  #buildAddToCartItems(option) {
    const bundleOption = this.#getSelectedOption();
    const properties = { _bundle: 'single-dual-bundle', _bundle_option: bundleOption };

    if (option === 'single') {
      const variantId = this.#getInitialVariantId();
      return variantId ? [{ id: parseInt(variantId, 10), quantity: 1, properties }] : [];
    }
    if (option === 'dual') {
      const v1 = this.#getVariantIdFromRadio(2);
      const v2 = this.#getVariantIdFromRadio(3);
      if (!v1 || !v2) return [];
      if (v1 === v2) {
        return [{ id: parseInt(v1, 10), quantity: 2, properties }];
      }
      return [
        { id: parseInt(v1, 10), quantity: 1, properties },
        { id: parseInt(v2, 10), quantity: 1, properties }
      ];
    }
    if (option === 'trio') {
      const v1 = this.#getVariantIdFromRadio(2);
      const v2 = this.#getVariantIdFromRadio(3);
      const v3 = this.#getVariantIdFromRadio(4);
      if (!v1 || !v2 || !v3) return [];
      const ids = [v1, v2, v3];
      const counts = {};
      for (const id of ids) {
        counts[id] = (counts[id] || 0) + 1;
      }
      return Object.entries(counts).map(([id, qty]) => ({
        id: parseInt(id, 10),
        quantity: qty,
        properties
      }));
    }
    return [];
  }

  async #addToCartMultiAPI(items) {
    console.log('Single/Dual Bundle: Cart API request - items:', items);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items,
        sections: 'mini-cart'
      }),
      credentials: 'include'
    });

    const text = await response.text();

    if (!response.ok) {
      let errorMessage = 'Failed to add to cart';
      try {
        const error = JSON.parse(text);
        console.error('Single/Dual Bundle: Cart API error response:', error);
        errorMessage = error.message || error.description || errorMessage;
      } catch (e) {
        console.error('Single/Dual Bundle: Cart API error text:', text, 'status:', response.status);
        if (response.status === 401 && !text) {
          errorMessage = 'Session expired or unauthorized. Please refresh the page and try again.';
        } else {
          errorMessage = text || errorMessage;
        }
      }
      throw new Error(errorMessage);
    }

    const result = JSON.parse(text);
    console.log('Single/Dual Bundle: Cart API success:', result);

    // Response includes full cart + sections when sections param is used
    return result;
  }

  async #refreshCart(cartDataFromAdd, options = {}) {
    const { openDrawer = true, showNotification = true } = typeof options === 'boolean' ? { openDrawer: options, showNotification: options } : options;
    try {
      let cartData;

      if (cartDataFromAdd && typeof cartDataFromAdd === 'object' && cartDataFromAdd.sections) {
        // Add response with sections: use if it has full cart (item_count), else merge sections into fresh cart
        if (typeof cartDataFromAdd.item_count === 'number') {
          cartData = cartDataFromAdd;
        } else {
          const cartResponse = await fetch('/cart.js');
          cartData = await cartResponse.json();
          if (cartData && typeof cartData === 'object') {
            cartData.sections = cartDataFromAdd.sections;
          }
        }
      } else {
        // Fallback: fetch cart and sections in parallel
        const [cartResponse, sectionsResponse] = await Promise.all([
          fetch('/cart.js'),
          fetch('/?sections=mini-cart')
        ]);
        cartData = await cartResponse.json();
        const sectionsData = await sectionsResponse.json();
        if (cartData && typeof cartData === 'object') {
          cartData.sections = sectionsData;
        }
      }

      if (cartData && typeof cartData === 'object') {
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartData }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartData,
            openMiniCart: openDrawer && window.themeVariables?.settings?.cartType === 'drawer'
          }
        }));
      }

      if (showNotification) {
        this.#showSuccessNotification();
      }

      // BOGOS applies discount/gift after our add; refresh again in ~1.5s to show it (drawer already open)
      if (showNotification) {
        setTimeout(() => this.#refreshCart(null, { openDrawer: false, showNotification: false }), 1500);
      }
    } catch (error) {
      console.error('Single/Dual Bundle: Error refreshing cart', error);
      const cartDrawer = document.querySelector('cart-drawer, [data-cart-drawer]');
      if (cartDrawer && window.themeVariables?.settings?.cartType === 'drawer') {
        cartDrawer.open = true;
      }
    }
  }

  #showSuccessNotification() {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #2e9e7b;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    notification.textContent = '✓ Zum Warenkorb hinzugefügt';
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.transition = 'opacity 0.3s';
      notification.style.opacity = '0';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  #showErrorMessage(message) {
    document.documentElement.dispatchEvent(new CustomEvent('product:add-error', {
      bubbles: true,
      detail: { message }
    }));
    alert(message);
  }

  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return `${amount.toFixed(2).replace('.', ',')} €`;
  }
}

const initSingleDualBundleSelectors = () => {
  document.querySelectorAll('[data-single-dual-bundle][data-single-dual-bundle-mode="replo"]').forEach(container => {
    if (container.dataset.singleDualBundleInitialized === 'true') {
      return;
    }

    container.dataset.singleDualBundleInitialized = 'true';
    new SingleDualBundleSelector(container);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSingleDualBundleSelectors);
} else {
  initSingleDualBundleSelectors();
}
